import { Building2, CalendarRange, Smartphone, FileSpreadsheet } from "lucide-react";
import { SEO } from "@/seo/useSeo";

export const CONTACT_EMAIL = "info@c-rossjobs.com";

const mailto = (subject, body) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
export const SETUP_MAILTO = mailto("Get my company set up on Cross Jobs",
  "Hi Cross Jobs team,\n\nI'd like to get my company set up.\n\nCompany name:\nTrade:\nNumber of crew:\nNumber of depots:\nBest phone number:\n\nThanks,");

// Company details kept in this tab while the user is away signing up with Google.
export const pendingSignup = {
  key: "cj_company_signup",
  set: (v) => sessionStorage.setItem(pendingSignup.key, JSON.stringify(v)),
  take: () => {
    const raw = sessionStorage.getItem(pendingSignup.key);
    sessionStorage.removeItem(pendingSignup.key);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  },
};

export const IMG = {
  hero: "https://images.unsplash.com/photo-1786859864999-2a969564ae2e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800",
  tradie: "https://images.unsplash.com/photo-1574757987642-5755f0839101?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100",
  estate: "https://images.unsplash.com/photo-1772325434696-14473ee3b53b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  melbourne: "https://images.unsplash.com/photo-1618023850386-588dd4a2f907?crop=entropy&cs=srgb&fm=jpg&q=80&w=1100",
  sydney: "https://images.pexels.com/photos/32889244/pexels-photo-32889244.jpeg?auto=compress&cs=tinysrgb&w=1900",
};

export const goTo = (id) => {
  const el = document.getElementById(id);
  if (el) return el.scrollIntoView({ behavior: "smooth", block: "start" });
  // Section lives on the home page: switch route without a reload, Home scrolls to the hash on mount.
  window.history.pushState({}, "", `/home#${id}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

export const apiError = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

export const TUTORIALS = [
  {
    id: "depots", title: "Depots & crew", icon: Building2, device: "desktop",
    blurb: "Set up your company, add each depot and invite your workers.",
    steps: [
      { title: "Start your company", caption: "Sign up with your company name and first depot. You land straight in your own dashboard — no setup call needed." },
      { title: "Add every depot", caption: "Open the Depots tab and add each yard or branch. Jobs, clients, rosters and timesheets stay separate per depot." },
      { title: "Invite your crew", caption: "Add workers by email, mark them casual or full-time and pick their home depot. Your crew is shared across the whole company." },
      { title: "Switch depots in one tap", caption: "Company logins flick between depots or see everything with All depots. Depot logins only ever see their own." },
    ],
  },
  {
    id: "roster", title: "Rosters & copying", icon: CalendarRange, device: "desktop",
    blurb: "Plan the week, let casuals accept shifts and copy last week in one click.",
    steps: [
      { title: "Plan the week", caption: "The roster follows your own pay settings, whichever day your week starts. Every worker gets a row, every day a column." },
      { title: "Drop in a shift", caption: "Click a day to put a worker on a job. They get a phone notification and see it in My Week straight away." },
      { title: "Casuals accept or decline", caption: "Casual shifts stay Pending until the worker taps Accept or Decline. Their answer shows on your roster instantly." },
      { title: "Copy last week", caption: "Same crew, same sites? Copy last week's roster in one click, then tweak the odd shift." },
    ],
  },
  {
    id: "clock", title: "Clocking on from the phone", icon: Smartphone, device: "phone",
    blurb: "Workers install the app, open today's job, clock on and send photos from site.",
    steps: [
      { title: "Install in seconds", caption: "No app store. Workers open the link on iPhone or Android and add Cross Jobs to their home screen." },
      { title: "Today's job, ready to go", caption: "The job card shows the address, client and notes. Workers tap Check in on-site when they arrive." },
      { title: "Clock on and off", caption: "One tap starts the shift timer. Breaks are tracked and the hours flow straight into your timesheets." },
      { title: "Photos as proof of work", caption: "Before, after and issue photos upload from site with captions, then the job is marked complete." },
    ],
  },
  {
    id: "payroll", title: "Timesheets, payroll & PDFs", icon: FileSpreadsheet, device: "desktop",
    blurb: "Filter every shift, approve the pay period and send a PDF to your bookkeeper.",
    steps: [
      { title: "Every shift in one list", caption: "Filter timesheets by worker, job or date range. Fix a missed clock-off or add a shift by hand." },
      { title: "Approve the pay period", caption: "Approvals group each worker's hours for your pay period, weekly or fortnightly, so you can check and approve them in one pass." },
      { title: "Payroll summary", caption: "Total hours per worker for the week, ready for wages — no spreadsheets, no adding up." },
      { title: "PDF straight to the bookkeeper", caption: "Download a branded payroll PDF or email it to your bookkeeper in one click." },
    ],
  },
];

export const FAQS = SEO.pages["/"].faqs;
